import {
  loadMarketIntelligenceMock,
  MARKET_INTELLIGENCE_USE_MOCK_DATA,
  MarketplaceIntelligenceBaseApiClient
} from '../marketplace-intelligence/client';
import type {
  AnalyticsQueryRequest,
  AnalyticsQueryResponse,
  AnalyticsRow,
  FactorInteractionsResponse,
  FactorSensitivityResponse
} from '@interfaces/Analytics.interface';

export class ApiAnalytics extends MarketplaceIntelligenceBaseApiClient {
  async queryView<T = AnalyticsRow>(
    req: AnalyticsQueryRequest
  ): Promise<AnalyticsQueryResponse<T>> {
    if (MARKET_INTELLIGENCE_USE_MOCK_DATA) {
      const mock = await loadMarketIntelligenceMock();
      return mock.getMockAnalyticsView<T>(
        req.study_id,
        req.view_name,
        req.filters
      );
    }
    return this.post<AnalyticsQueryResponse<T>>('/api/v1/analytics/query', {
      filters: {},
      format: 'json',
      ...req
    });
  }

  async query<T = AnalyticsRow>(req: AnalyticsQueryRequest): Promise<T[]> {
    const res = await this.queryView<T>(req);
    return Array.isArray(res) ? res : res.data ?? [];
  }

  async views(): Promise<string[]> {
    if (MARKET_INTELLIGENCE_USE_MOCK_DATA) {
      return [
        'v_brand_competitiveness',
        'v_price_curves',
        'v_rated_variables',
        'v_cross_factor_interactions',
        'v_persona_quotes',
        'v_fatal_combinations',
        'v_factor_sensitivity',
        'v_training_data'
      ];
    }
    const res = await this.get<string[] | { views: string[] }>(
      '/api/v1/analytics/views'
    );
    return Array.isArray(res) ? res : res.views ?? [];
  }

  /** Posts the rows to export; the API answers with `text/csv`. */
  async exportCsv(rows: AnalyticsRow[]): Promise<Blob | string> {
    if (MARKET_INTELLIGENCE_USE_MOCK_DATA) {
      if (!rows.length)
        return new Blob([''], { type: 'text/csv;charset=utf-8' });
      const cols = Object.keys(rows[0]);
      const escape = (v: unknown) => {
        if (v == null) return '';
        const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
        return s.includes(',') || s.includes('"') || s.includes('\n')
          ? `"${s.replace(/"/g, '""')}"`
          : s;
      };
      const csv = [
        cols.join(','),
        ...rows.map((r) => cols.map((c) => escape(r[c])).join(','))
      ].join('\n');
      return new Blob([csv], { type: 'text/csv;charset=utf-8' });
    }
    return this.post<string>('/api/v1/analytics/export/csv', { rows });
  }

  async getFactorSensitivity(
    studyId: string,
    byBrand = true
  ): Promise<FactorSensitivityResponse> {
    if (MARKET_INTELLIGENCE_USE_MOCK_DATA) {
      const mock = await loadMarketIntelligenceMock();
      return mock.getMockFactorSensitivity(studyId);
    }
    return this.get<FactorSensitivityResponse>(
      `/api/v1/analytics/factor-sensitivity/${encodeURIComponent(studyId)}`,
      { params: byBrand ? { by: 'brand' } : undefined }
    );
  }

  factorSensitivity(
    studyId: string,
    byBrand = true
  ): Promise<FactorSensitivityResponse> {
    return this.getFactorSensitivity(studyId, byBrand);
  }

  async getFactorInteractions(
    studyId: string
  ): Promise<FactorInteractionsResponse> {
    if (MARKET_INTELLIGENCE_USE_MOCK_DATA) {
      const mock = await loadMarketIntelligenceMock();
      return mock.getMockFactorInteractions(studyId);
    }
    return this.get<FactorInteractionsResponse>(
      `/api/v1/studies/${encodeURIComponent(studyId)}/factor-interactions`
    );
  }
}

export { analyticsQueryKeys } from './query-keys';
export * from './use-analytics-queries';
export { ApiAnalytics as AnalyticsApi };
